import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  Shield, 
  Database, 
  Users, 
  Store, 
  Tag, 
  Trash2, 
  RefreshCw,
  Search,
  AlertTriangle,
  CheckCircle2,
  Lock,
  LogOut,
  Settings,
  Eye,
  EyeOff
} from 'lucide-react';

// Admin credentials
const ADMIN_USERNAME = 'delfieeadmin';
const ADMIN_PASSWORD = 'ATes951753Mar*';

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  const [stats, setStats] = useState(null);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [teamFeatures, setTeamFeatures] = useState(null);
  
  // Raw query state
  const [queryCollection, setQueryCollection] = useState('brands');
  const [queryFilter, setQueryFilter] = useState('{}');
  const [queryResults, setQueryResults] = useState(null);

  // Check if already logged in
  useEffect(() => {
    const adminAuth = localStorage.getItem('adminAuth');
    if (adminAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadStats();
      loadTeams();
    }
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginUsername === ADMIN_USERNAME && loginPassword === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem('adminAuth', 'true');
      setLoginError('');
      toast.success('Admin girişi başarılı');
    } else {
      setLoginError('Kullanıcı adı veya şifre hatalı');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('adminAuth');
    setLoginUsername('');
    setLoginPassword('');
    toast.info('Çıkış yapıldı');
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
            <CardTitle>Admin Paneli</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label>Kullanıcı Adı</Label>
                <Input
                  type="text"
                  value={loginUsername}
                  onChange={(e) => setLoginUsername(e.target.value)}
                  placeholder="Kullanıcı adı"
                  required
                />
              </div>
              <div>
                <Label>Şifre</Label>
                <Input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Şifre"
                  required
                />
              </div>
              {loginError && (
                <p className="text-red-500 text-sm">{loginError}</p>
              )}
              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
                <Lock className="w-4 h-4 mr-2" />
                Giriş Yap
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/admin/stats');
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Admin stats error:', err);
      // Don't show error for 403/401, just means not logged in
      if (err.response?.status !== 403 && err.response?.status !== 401) {
        setError(err.response?.data?.detail || 'Failed to load stats');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTeams = async () => {
    try {
      const response = await axiosInstance.get('/admin/teams');
      setTeams(response.data);
    } catch (err) {
      console.error('Failed to load teams:', err);
    }
  };

  const loadTeamBrands = async (teamId) => {
    try {
      const response = await axiosInstance.get(`/admin/teams/${teamId}/brands`);
      setBrands(response.data);
    } catch (err) {
      toast.error('Failed to load brands');
    }
  };

  const loadTeamCategories = async (teamId) => {
    try {
      const response = await axiosInstance.get(`/admin/categories/${teamId}`);
      setCategories(response.data);
    } catch (err) {
      toast.error('Failed to load categories');
    }
  };

  const selectTeam = (team) => {
    setSelectedTeam(team);
    loadTeamBrands(team.id);
    loadTeamCategories(team.id);
    loadTeamFeatures(team.id);
  };

  const loadTeamFeatures = async (teamId) => {
    try {
      const response = await axiosInstance.get(`/admin/teams/${teamId}/features`);
      setTeamFeatures(response.data.features);
    } catch (err) {
      console.error('Failed to load features:', err);
      setTeamFeatures(null);
    }
  };

  const updateFeature = async (featureKey, enabled) => {
    if (!selectedTeam) return;
    
    try {
      await axiosInstance.put(`/admin/teams/${selectedTeam.id}/features`, {
        features: { [featureKey]: enabled }
      });
      setTeamFeatures(prev => ({ ...prev, [featureKey]: enabled }));
      toast.success(`${featureKey} ${enabled ? 'aktif' : 'pasif'} edildi`);
    } catch (err) {
      toast.error('Özellik güncellenemedi');
    }
  };

  const FEATURE_LABELS = {
    product_upload: { label: 'Ürün Yükleme', icon: '📦' },
    video_audio: { label: 'Video + Ses', icon: '🎬' },
    video_match: { label: 'Video Eşleştirme', icon: '🔗' },
    data_match: { label: 'Veri Eşleştirme', icon: '📊' },
    file_rename: { label: 'Dosya Yeniden Adlandırma', icon: '📝' },
    announcements: { label: 'Duyurular', icon: '📢' },
    shopify: { label: 'Shopify Entegrasyonu', icon: '🛒' },
    auto_menu: { label: 'Otomatik Menü', icon: '⏰' }
  };

  const deleteBrand = async (brandId, brandName) => {
    if (!window.confirm(`Delete brand "${brandName}"?`)) return;
    
    try {
      await axiosInstance.delete(`/admin/brands/${brandId}`);
      toast.success(`Brand "${brandName}" deleted`);
      loadTeamBrands(selectedTeam.id);
      loadStats();
    } catch (err) {
      toast.error('Failed to delete brand');
    }
  };

  const deleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Delete category "${categoryName}"?`)) return;
    
    try {
      await axiosInstance.delete(`/admin/categories/${categoryId}`);
      toast.success(`Category "${categoryName}" deleted`);
      loadTeamCategories(selectedTeam.id);
      loadStats();
    } catch (err) {
      toast.error('Failed to delete category');
    }
  };

  const fixBrands = async () => {
    if (!selectedTeam) return;
    
    try {
      const response = await axiosInstance.post(`/admin/fix-brands/${selectedTeam.id}`);
      toast.success(`Fixed: ${response.data.fixed}, Deleted: ${response.data.deleted}`);
      loadTeamBrands(selectedTeam.id);
      loadStats();
    } catch (err) {
      toast.error('Failed to fix brands');
    }
  };

  const executeQuery = async () => {
    try {
      const filter = JSON.parse(queryFilter);
      const response = await axiosInstance.post('/admin/raw-query', null, {
        params: { collection: queryCollection, query: JSON.stringify(filter) }
      });
      setQueryResults(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Query failed');
    }
  };

  const executeDelete = async () => {
    if (!window.confirm('Are you sure you want to delete matching documents?')) return;
    
    try {
      const filter = JSON.parse(queryFilter);
      const response = await axiosInstance.delete('/admin/raw-delete', {
        params: { collection: queryCollection, query: JSON.stringify(filter) }
      });
      toast.success(`Deleted ${response.data.deleted_count} documents`);
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <AlertTriangle className="w-16 h-16 text-red-500" />
        <h1 className="text-2xl font-bold text-red-600">{error}</h1>
        <p className="text-gray-500">Bir hata oluştu. Lütfen tekrar deneyin.</p>
        <Button onClick={() => { setError(null); loadStats(); loadTeams(); }} className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" />
          Tekrar Dene
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-purple-600" />
          <h1 className="text-3xl font-bold">Admin Panel</h1>
        </div>
        <Button variant="outline" onClick={handleLogout} className="text-red-600 hover:bg-red-50">
          <LogOut className="w-4 h-4 mr-2" />
          Çıkış
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Store className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.teams}</p>
                  <p className="text-xs text-gray-500">Teams</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.users}</p>
                  <p className="text-xs text-gray-500">Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.brands}</p>
                  <p className="text-xs text-gray-500">Brands</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Database className="w-5 h-5 text-orange-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.categories}</p>
                  <p className="text-xs text-gray-500">Categories</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Database className="w-5 h-5 text-pink-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.products}</p>
                  <p className="text-xs text-gray-500">Products</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Database className="w-5 h-5 text-cyan-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.shopify_settings}</p>
                  <p className="text-xs text-gray-500">Shopify</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-teal-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.shopify_processed}</p>
                  <p className="text-xs text-gray-500">Processed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Teams List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              Teams ({teams.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {teams.map(team => (
                <button
                  key={team.id}
                  onClick={() => selectTeam(team)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedTeam?.id === team.id 
                      ? 'bg-blue-50 border-blue-300' 
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <p className="font-medium">{team.name}</p>
                  <p className="text-xs text-gray-500 truncate">{team.id}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Brands */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Tag className="w-5 h-5" />
                Brands ({brands.length})
              </span>
              {selectedTeam && (
                <Button size="sm" variant="outline" onClick={fixBrands}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Fix
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedTeam ? (
              <p className="text-gray-500 text-center py-8">Select a team</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {brands.map(brand => (
                  <div
                    key={brand.id}
                    className="flex items-center justify-between p-2 rounded border hover:bg-gray-50"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{brand.name || '(no name)'}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {brand.image_url ? '✅ Image' : '❌ No image'} | 
                        {brand.link ? ' ✅ Link' : ' ❌ No link'}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => deleteBrand(brand.id, brand.name)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Categories */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Categories ({categories.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedTeam ? (
              <p className="text-gray-500 text-center py-8">Select a team</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {categories.map(cat => (
                  <div
                    key={cat.id}
                    className="flex items-center justify-between p-2 rounded border hover:bg-gray-50"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{cat.name || '(no name)'}</p>
                      <p className="text-xs text-gray-500 truncate">
                        Channel: {cat.channel_id || 'none'}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => deleteCategory(cat.id, cat.name)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Feature Management */}
      {selectedTeam && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Özellik Yönetimi - {selectedTeam.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {teamFeatures ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(FEATURE_LABELS).map(([key, { label, icon }]) => (
                  <div key={key} className="flex items-center justify-between p-3 rounded-lg border bg-gray-50">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{icon}</span>
                      <span className="text-sm font-medium">{label}</span>
                    </div>
                    <Switch
                      checked={teamFeatures[key] ?? true}
                      onCheckedChange={(checked) => updateFeature(key, checked)}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">Yükleniyor...</p>
            )}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <Eye className="w-4 h-4 inline mr-2" />
              Aktif özellikler mağaza menüsünde görünür. Pasif özellikler gizlenir.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Raw Query */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Raw Database Query
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 mb-4">
            <div>
              <Label>Collection</Label>
              <Input
                value={queryCollection}
                onChange={(e) => setQueryCollection(e.target.value)}
                placeholder="brands"
                className="w-40"
              />
            </div>
            <div className="flex-1">
              <Label>Query (JSON)</Label>
              <Input
                value={queryFilter}
                onChange={(e) => setQueryFilter(e.target.value)}
                placeholder='{"name": "QUINTO"}'
              />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={executeQuery}>
                <Search className="w-4 h-4 mr-1" />
                Query
              </Button>
              <Button variant="destructive" onClick={executeDelete}>
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
          
          {queryResults && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">
                Found {queryResults.count} documents in {queryResults.collection}
              </p>
              <pre className="text-xs overflow-auto max-h-64 bg-white p-2 rounded border">
                {JSON.stringify(queryResults.results, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
